/** Vtiger-style list table: sortable columns, per-column filter row,
 *  row checkboxes, classic "1 to 20 of N" pagination and per-user
 *  saved views (pass `viewKey` to enable). */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ArrowUpDown, Search, Bookmark, Trash2, X } from "lucide-react";

export function VtigerTable({ columns, rows, onRowClick, pageSize = 20,
    testid = "vtable", viewKey, checkbox = true, onCheckedChange, filterNote, summary, layout = "fixed" }) {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState(null);       // {key, dir}
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState(new Set());
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("");
  const [saveName, setSaveName] = useState(null); // null = closed

  useEffect(() => {
    if (!viewKey) return;
    api.get(`/table-views/${viewKey}`)
      .then(({ data }) => setViews(data.items || [])).catch(() => {});
  }, [viewKey]);

  const updateChecked = (next) => {
    setChecked(next);
    onCheckedChange && onCheckedChange([...next]);
  };

  const applyView = (id) => {
    setActiveView(id);
    setPage(1);
    const v = views.find((x) => x.id === id);
    if (v) { setFilters(v.filters || {}); setSort(v.sort || null); }
    else { setFilters({}); setSort(null); }
  };

  const saveView = async () => {
    const name = (saveName || "").trim();
    if (!name) return toast.error("View name required");
    try {
      const { data } = await api.post(`/table-views/${viewKey}`, { name, filters, sort });
      setViews((v) => [...v.filter((x) => x.id !== data.item.id), data.item]
        .sort((a, b) => a.name.localeCompare(b.name)));
      setActiveView(data.item.id);
      setSaveName(null);
      toast.success(`View "${name}" saved`);
    } catch { toast.error("Failed to save view"); }
  };

  const deleteView = async () => {
    if (!activeView) return;
    try {
      await api.delete(`/table-views/${viewKey}/${activeView}`);
      setViews((v) => v.filter((x) => x.id !== activeView));
      applyView("");
      toast.success("View deleted");
    } catch { toast.error("Failed to delete view"); }
  };

  const val = (r, c) => {
    const v = c.value ? c.value(r) : r[c.key];
    return v == null ? "" : String(v);
  };

  const filtered = useMemo(() => {
    let out = rows;
    for (const c of columns) {
      const f = (filters[c.key] || "").trim().toLowerCase();
      if (f) out = out.filter((r) => val(r, c).toLowerCase().includes(f));
    }
    if (sort) {
      const c = columns.find((x) => x.key === sort.key);
      if (c) out = [...out].sort((a, b) => {
        const av = c.num ? Number(c.value ? c.value(a) : a[c.key]) || 0 : val(a, c).toLowerCase();
        const bv = c.num ? Number(c.value ? c.value(b) : b[c.key]) || 0 : val(b, c).toLowerCase();
        return (av > bv ? 1 : av < bv ? -1 : 0) * (sort.dir === "asc" ? 1 : -1);
      });
    }
    return out;
  }, [rows, filters, sort, columns]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pages);
  const slice = filtered.slice((cur - 1) * pageSize, cur * pageSize);
  const from = filtered.length === 0 ? 0 : (cur - 1) * pageSize + 1;
  const to = Math.min(cur * pageSize, filtered.length);

  const toggleAll = () => {
    if (checked.size === slice.length) updateChecked(new Set());
    else updateChecked(new Set(slice.map((r) => r.id)));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid={testid}>
      <div className="sticky left-0 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 px-3 py-1.5 border-b border-slate-100 text-[11px] text-slate-500 [&>*]:whitespace-nowrap">
        {viewKey && (
          <span className="mr-auto inline-flex items-center gap-1.5">
            <Bookmark className="h-3.5 w-3.5 text-emerald-700" />
            <select value={activeView} onChange={(e) => applyView(e.target.value)}
                data-testid={`${testid}-view-select`}
                className="h-6 px-1 text-[11px] border border-slate-200 rounded max-w-[160px]">
              <option value="">All records</option>
              {views.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            {saveName === null ? (
              <button onClick={() => setSaveName("")} data-testid={`${testid}-save-view-btn`}
                  className="px-1.5 py-0.5 rounded border border-slate-200 hover:bg-emerald-50 text-emerald-800">
                Save view</button>
            ) : (
              <span className="inline-flex items-center gap-1">
                <input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveView()}
                    placeholder="View name" data-testid={`${testid}-view-name-input`}
                    className="h-6 w-28 px-1.5 text-[11px] border border-emerald-300 rounded" />
                <button onClick={saveView} data-testid={`${testid}-view-name-save`}
                    className="px-1.5 py-0.5 rounded bg-emerald-700 text-white">Save</button>
                <button onClick={() => setSaveName(null)} className="p-0.5 text-slate-400"><X className="h-3 w-3" /></button>
              </span>
            )}
            {activeView && (
              <button onClick={deleteView} data-testid={`${testid}-delete-view-btn`}
                  className="p-1 rounded border border-slate-200 hover:bg-rose-50 text-rose-600">
                <Trash2 className="h-3 w-3" /></button>)}
          </span>
        )}
        {checked.size > 0 && <span className={"text-emerald-700 font-medium " + (viewKey ? "" : "mr-auto")}>{checked.size} selected</span>}
        {filterNote && Object.values(filters).some((v) => (v || "").trim()) && (
          <span className="text-amber-700" data-testid={`${testid}-filter-note`}>{filterNote}</span>)}
        {summary && <span className={"inline-flex items-center " + ((!viewKey && checked.size === 0) ? "mr-auto" : "")} data-testid={`${testid}-summary`}>{summary}</span>}
        <span data-testid={`${testid}-range`}>{from} to {to} of {filtered.length}</span>
        <button onClick={() => setPage(Math.max(1, cur - 1))} disabled={cur <= 1}
            data-testid={`${testid}-prev`}
            className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronLeft className="h-3.5 w-3.5" /></button>
        <button onClick={() => setPage(Math.min(pages, cur + 1))} disabled={cur >= pages}
            data-testid={`${testid}-next`}
            className="p-1 rounded border border-slate-200 disabled:opacity-30"><ChevronRight className="h-3.5 w-3.5" /></button>
      </div>
      <table className={"w-full text-[12px] " + (layout === "auto" ? "" : "table-fixed")}
        style={layout === "auto" ? undefined : { minWidth: Math.max(960, (checkbox ? 32 : 0) + columns.reduce((s, c) => s + (c.width || 220), 0)) }}>
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            {checkbox && (
              <th className="w-8 px-2 py-2">
                <input type="checkbox" checked={slice.length > 0 && checked.size === slice.length}
                    onChange={toggleAll} data-testid={`${testid}-check-all`} /></th>)}
            {columns.map((c) => (
              <th key={c.key} style={c.width ? { width: c.width } : {}}
                  className={"px-2 py-2 font-semibold text-slate-600 whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer select-none " +
                    (c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left")}
                  onClick={() => setSort((s) => ({ key: c.key,
                    dir: s?.key === c.key && s.dir === "asc" ? "desc" : "asc" }))}
                  data-testid={`${testid}-sort-${c.key}`}>
                <span className={"inline-flex items-center gap-1 " + (c.align === "center" ? "justify-center w-full" : "")}>
                  <ArrowUpDown className={"h-3 w-3 " +
                    (sort?.key === c.key ? "text-emerald-700" : "text-slate-300")} />
                  {c.label}
                </span>
              </th>))}
          </tr>
          <tr className="border-b border-slate-200">
            {checkbox && <th className="px-2 py-1.5"><Search className="h-3.5 w-3.5 text-emerald-700" /></th>}
            {columns.map((c) => (
              <th key={c.key} className="px-1.5 py-1.5">
                {c.filter !== false && (
                  <input value={filters[c.key] || ""}
                      onChange={(e) => { setFilters({ ...filters, [c.key]: e.target.value }); setPage(1); }}
                      data-testid={`${testid}-filter-${c.key}`}
                      className="w-full h-7 px-1.5 text-[11px] font-normal border border-slate-200 rounded" />)}
              </th>))}
          </tr>
        </thead>
        <tbody>
          {slice.length === 0 && (
            <tr><td colSpan={columns.length + (checkbox ? 1 : 0)}
                className="p-8 text-center text-slate-400">No matching records.</td></tr>)}
          {slice.map((r) => (
            <tr key={r.id} onClick={() => onRowClick && onRowClick(r)}
                data-testid={`${testid}-row-${r.id}`}
                className="border-b border-slate-100 hover:bg-emerald-50/40 cursor-pointer">
              {checkbox && (
                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={checked.has(r.id)}
                      onChange={() => { const n = new Set(checked);
                        n.has(r.id) ? n.delete(r.id) : n.add(r.id); updateChecked(n); }} /></td>)}
              {columns.map((c) => (
                <td key={c.key}
                    className={"px-2 py-2 align-middle overflow-hidden " +
                      (c.align === "right" ? "text-right font-mono " : c.align === "center" ? "text-center " : "") +
                      (c.mono ? "font-mono " : "") + (c.cls || "")}>
                  {c.render ? c.render(r) : (val(r, c) || "—")}
                </td>))}
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}
